import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** When this value changes (e.g. the route path), a captured error is cleared. */
  resetKey?: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Admin route crashed', error, info.componentStack);
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main role="alert" className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-zinc-100 mb-2">
            Something went wrong on this page
          </h1>
          <p className="text-zinc-400 text-sm mb-2">
            The rest of the admin app still works. You can retry, or head back to the dashboard.
          </p>
          <p className="text-zinc-500 text-xs mb-6 break-words">{error.message}</p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.reset}
              aria-label="Try loading this page again"
              className="px-6 py-3 bg-violet-600 hover:bg-violet-500 text-zinc-950 font-medium rounded-lg transition-colors min-h-[48px]"
            >
              Try again
            </button>
            <Link
              to="/dashboard"
              className="inline-flex items-center px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-medium rounded-lg transition-colors min-h-[48px]"
            >
              Go to dashboard
            </Link>
          </div>
        </div>
      </main>
    );
  }
}
